import {
  boot,
  buildAttributes,
  isIntercomAvailable,
  loadIntercom,
  setAuthTokens,
  show,
  update,
} from '@usealloy/intercom';
import type { IntercomSettings } from '@usealloy/intercom';

/** Unauthenticated Intercom config. Serves the public app ID only -- no secret, no JWT. */
const CONFIG_URL = '/v3/intercom_config/';

interface IntercomConfigResponse {
  /** Null when the environment has no Intercom credentials configured. */
  app_id: string | null;
  customer: {
    organization_name: string;
    organization_name_slug: string;
  } | null;
  environment: string;
}

/** Reads the config, or null if the endpoint could not be reached. Never throws. */
const fetchIntercomConfig = async (): Promise<IntercomConfigResponse | null> => {
  try {
    // AjaxOnlyMixin 404s without this header.
    const response = await fetch(CONFIG_URL, {
      headers: { 'X-Requested-With': 'XMLHttpRequest' },
    });

    if (!response.ok) return null;

    return await response.json();
  } catch {
    return null;
  }
};

/**
 * Loads the support widget for pages extending daffy/templates/base.html -- the auth flow
 * and the 403/404/500 pages.
 *
 * These pages have no agent, so the app ID is read anonymously from /v3/intercom_config/.
 * That needs a fetch, which is why the snippet is not a static tag here as it is in
 * v3/templates/dashboard/index.html. Intercom boots without a JWT, creating a Lead that
 * merges into the User record at sign-in.
 */
export const loadPublicSupportWidget = async (): Promise<void> => {
  if (isIntercomAvailable()) return;

  const config = await fetchIntercomConfig();

  // Unreachable is not "off" -- assume this environment is configured until told otherwise.
  if (!config?.app_id) return;

  // loadIntercom first: it installs the stub that queues this boot.
  loadIntercom(config.app_id);
  boot(
    buildAttributes({
      customerName: config.customer?.organization_name,
      customerSlug: config.customer?.organization_name_slug,
      environment: config.environment,
    }),
  );
};

export type IntercomPositionOverride = Pick<
IntercomSettings,
'alignment' | 'horizontal_padding' | 'vertical_padding'
>;

interface OpenSupportProps {
  /**
   * Alloy-signed Fin Data Connector JWT from the latest user-context refetch.
   * Presented via `setAuthTokens` at open time so Intercom holds a fresh token.
   */
  connectorJwt?: string | null;
  intercomPositionOverride?: IntercomPositionOverride;
  /**
   * Signed Intercom identity token for the current agent. Read fresh at call time rather
   * than captured earlier -- `exp` follows the Alloy session, so a token minted at boot can
   * be expired by the time someone clicks. Omit on the login page, which is anonymous.
   */
  userJwt?: string;
  /**
   * Called when Intercom's `update` allowance for this page (20 per 30 minutes) is spent.
   * Should essentially never fire -- see `@usealloy/intercom`'s `update` docs -- so a call
   * means a trigger is mis-wired, not something the person using the dashboard caused.
   * `show()` is not called in this case, since Intercom kept its last context and opening
   * now would start a conversation pointing at the wrong screen.
   */
  onRateLimited?: () => void;
}

/** Hands `connector_jwt` to Intercom for Fin Data Connector auth. No-op when absent. */
const presentConnectorJwt = (connectorJwt?: string | null): void => {
  if (!connectorJwt) return;
  setAuthTokens({ connector_jwt: connectorJwt });
};

/**
 * Opens the Messenger.
 *
 * `update` refreshes Intercom's context and reports whether that landed. `not-loaded`
 * means Intercom hasn't booted on this page yet, so there's nothing to open.
 */
export const openSupport = ({
  connectorJwt,
  intercomPositionOverride,
  userJwt,
  onRateLimited,
}: OpenSupportProps = {}) => {
  // Passenger tokens: present the latest values at open time, not on every refetch.
  presentConnectorJwt(connectorJwt);

  const outcome = update({
    ...(userJwt ? { intercom_user_jwt: userJwt } : undefined),
    ...intercomPositionOverride,
  });

  if (outcome === 'not-loaded') return;

  if (outcome === 'rate-limited') {
    onRateLimited?.();
    return;
  }

  show();
};

export default {
  loadPublicSupportWidget,
  openSupport,
};
